import React from "react";
import Box from "../Box/Box";
import GreenCheckSVG from "../../../assets/media/svg/icons/green_check.svg";
import DynamicIframe from "@components/DynamicIframe/DynamicIframe.tsx";

interface USPItem {
  title?: string;
  icon?: string;
}

interface ToolDetailBannerProps {
  clinicSectionTitle?: string;
  clinicSectionDescription?: string;
  clinicSectionUSP?: USPItem[];
  clinicSectionButtonLabel?: string;
  clinicSectionButtonLink?: string;
  clinicSectionButtonDestination?: string;
  clinicSectionButtonForm?: string;
  clinicSectionButtonFormPage?: string;
  clinicSectionImage?: string;
  emailParams: Record<string, any>;
  fullLink: string;
}

const ToolDetailBanner: React.FC<ToolDetailBannerProps> = ({
  clinicSectionTitle,
  clinicSectionDescription,
  clinicSectionUSP,
  clinicSectionButtonLabel,
  clinicSectionButtonLink,
  clinicSectionButtonDestination,
  clinicSectionButtonForm,
  clinicSectionButtonFormPage,
  clinicSectionImage,
  emailParams,
  fullLink,
}) => {
  // Extracting validation logic for clinicSectionButtonDestination
  const isPageDestination = clinicSectionButtonDestination === "page";
  const isModalDestination = clinicSectionButtonDestination === "modal";
  const isInplaceDestination = clinicSectionButtonDestination === "inplace";

  const hasPageConfig =
    isPageDestination && clinicSectionButtonLink && clinicSectionButtonLabel;
  const hasModalConfig =
    isModalDestination && clinicSectionButtonForm && clinicSectionButtonLabel;
  const hasInplaceConfig = isInplaceDestination && clinicSectionButtonFormPage;

  const maxChar = 40;

  const getCtaButtonProps = () => {
    const base = { className: "btn btn--blue-90" };
    if (isModalDestination) {
      return {
        ...base,
        as: "button",
        "data-module": "modal",
        "data-form-id": clinicSectionButtonForm,
      };
    }
    return {
      ...base,
      href: fullLink,
    };
  };
  // Max length for USP titles based on whether an image is selected
  const maxUspCharLimit = clinicSectionImage ? maxChar : null;

  // Function to handle title truncation
  const truncateUspTitle = (title?: string): string => {
    if (title && maxUspCharLimit) {
      // First, check if the title is longer than the max limit
      if (title.length > maxUspCharLimit) {
        // Truncate to max character limit
        let truncated = title.slice(0, maxUspCharLimit);

        // Find the last space within the limit to avoid cutting words in half
        const lastSpaceIndex = truncated.lastIndexOf(" ");

        // If there is a space, truncate at the last whole word
        if (lastSpaceIndex !== -1) {
          truncated = truncated.slice(0, lastSpaceIndex);
        }

        return truncated;
      }
      return title;
    }
    return title || "";
  };
  return (
    <>
      <Box backgroundColor="blue-5" className="cta-tool">
        <div className="grid grid-cols-1 gap-4 md:gap-6 md:grid-cols-12">
          <div
            className={`gap-6 flex flex-col items-stretch md:flex-row md:col-span-12`}
          >
            <div className="flex flex-col gap-6 w-auto">
              {(clinicSectionTitle || clinicSectionDescription) && (
                <div className="cta-tool__header">
                  {clinicSectionTitle && (
                    <div className="cta-tool__title">{clinicSectionTitle}</div>
                  )}

                  {clinicSectionDescription && (
                    <div className="cta-tool__text">
                      {clinicSectionDescription}
                    </div>
                  )}
                </div>
              )}
              {clinicSectionUSP && clinicSectionUSP.length > 0 && (
                <div className="flex flex-col flex-nowrap md:flex-row md:flex-wrap gap-3 md:gap-x-6">
                  {clinicSectionUSP.map((usp, index) =>
                    usp.title ? (
                      <div
                        key={index}
                        className="flex flex-row flex-nowrap justify-start items-center gap-1 text-base"
                      >
                        <GreenCheckSVG />
                        <h6 className="text-base">
                          {truncateUspTitle(usp.title)}
                        </h6>
                      </div>
                    ) : null,
                  )}
                </div>
              )}

              {(hasPageConfig || hasModalConfig) && !isInplaceDestination ? (
                <div className={"cta-tool__buttons md:flex items-center"}>
                  {hasPageConfig && (
                    <a {...getCtaButtonProps()}>{clinicSectionButtonLabel}</a>
                  )}
                  {hasModalConfig && (
                    <button
                      {...getCtaButtonProps()}
                      type="button"
                      data-form-id={clinicSectionButtonForm}
                      data-modal-type="tool-send-clinic"
                      data-form-params={JSON.stringify(emailParams)}
                    >
                      {clinicSectionButtonLabel}
                    </button>
                  )}
                </div>
              ) : null}
            </div>
            {clinicSectionImage && !isInplaceDestination && (
              <div className="cta-tool__image flex flex-col items-center justify-center md:w-auto">
                <img
                  src={clinicSectionImage}
                  alt={clinicSectionTitle}
                  className="aspect-square"
                />
              </div>
            )}
          </div>

          {hasInplaceConfig ? (
            <div
              className={
                "cta-tool__buttons md:flex items-center md:col-span-12  " +
                (clinicSectionTitle || clinicSectionDescription)
              }
            >
              <DynamicIframe
                src={`${clinicSectionButtonFormPage}?${new URLSearchParams(emailParams)}`}
                title={clinicSectionTitle}
              />
            </div>
          ) : null}
        </div>
      </Box>
    </>
  );
};

export default ToolDetailBanner;
